import { Fragment, useState } from "react";
import { Search, ChevronDown, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";

export function DataTable({ columns, rows, loading, testId = "data-table", searchKeys = [], actions, emptyText = "Belum ada data", selected, onToggle, onToggleAll, groupBy, selectable: rowSelectable = () => true, rowClassName }) {
  const [q, setQ] = useState("");
  const [collapsed, setCollapsed] = useState(() => new Set());
  const filtered = q
    ? rows.filter((r) => searchKeys.some((k) => String(r[k] ?? "").toLowerCase().includes(q.toLowerCase())))
    : rows;
  const selectable = !!selected;
  const allChecked = selectable && filtered.some(rowSelectable) && filtered.filter(rowSelectable).every((r) => selected.has(r.id));
  const colSpan = columns.length + (selectable ? 1 : 0) + (actions ? 1 : 0);
  const groups = groupBy ? [...filtered.reduce((m, r) => { const g = groupBy(r) || "Tanpa Kategori"; m.set(g, [...(m.get(g) || []), r]); return m; }, new Map())].sort((a, b) => a[0].localeCompare(b[0])) : [[null, filtered]];
  const toggleGroup = (g) => setCollapsed((s) => { const n = new Set(s); n.has(g) ? n.delete(g) : n.add(g); return n; });
  const renderRow = (r, i) => (
    <TableRow key={r.id || i} data-testid={`${testId}-row-${i}`} data-state={selectable && selected.has(r.id) ? "selected" : undefined} className={cn("group", rowClassName?.(r))}>
      {selectable && <TableCell>{rowSelectable(r) ? <Checkbox checked={selected.has(r.id)} onCheckedChange={() => onToggle(r.id)} aria-label="Pilih baris" data-testid={`${testId}-select-${r.id}`} /> : <span className="block h-4 w-4" data-testid={`${testId}-protected-${r.id}`} />}</TableCell>}
      {columns.map((c) => <TableCell key={c.key} className="text-sm whitespace-nowrap">{c.render ? c.render(r) : (r[c.key] ?? "—")}</TableCell>)}
      {actions && <TableCell className="text-right"><div className="flex justify-end gap-1">{actions(r)}</div></TableCell>}
    </TableRow>
  );
  return (
    <div className="op-card overflow-hidden" data-testid={testId}>
      {searchKeys.length > 0 && (
        <div className="flex items-center gap-2 border-b p-3">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari..." className="h-8 max-w-xs border-0 bg-transparent focus-visible:ring-0" data-testid={`${testId}-search`} />
          <span className="ml-auto data-label">{selectable && selected.size > 0 ? `${selected.size} dipilih · ` : ""}{filtered.length} rows</span>
        </div>
      )}
      <div className="overflow-x-auto scrollbar-thin">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {selectable && <TableHead className="w-10"><Checkbox checked={allChecked} onCheckedChange={(v) => onToggleAll(filtered.filter(rowSelectable).map((r) => r.id), !!v)} aria-label="Pilih semua" data-testid={`${testId}-select-all`} /></TableHead>}
              {columns.map((c) => <TableHead key={c.key} className="data-label whitespace-nowrap">{c.label}</TableHead>)}
              {actions && <TableHead className="data-label text-right">Aksi</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={colSpan} className="py-10 text-center text-muted-foreground">Memuat...</TableCell></TableRow>}
            {!loading && filtered.length === 0 && <TableRow><TableCell colSpan={colSpan} className="py-10 text-center text-muted-foreground">{emptyText}</TableCell></TableRow>}
            {!loading && groups.map(([g, items]) => (
              <Fragment key={g ?? "_all"}>
                {g !== null && (
                  <TableRow className="bg-muted/60 hover:bg-muted/80 cursor-pointer" onClick={() => toggleGroup(g)} data-testid={`${testId}-group-${g.replace(/[^a-zA-Z0-9]+/g, "-")}`}>
                    <TableCell colSpan={colSpan} className="py-2">
                      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
                        {collapsed.has(g) ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                        <span className="h-3 w-0.5 rounded bg-primary" />{g}<span className="font-mono font-normal text-muted-foreground">({items.length})</span>
                        {selectable && <Checkbox className="ml-2" checked={items.filter(rowSelectable).every((r) => selected.has(r.id))} onClick={(e) => e.stopPropagation()} onCheckedChange={(v) => onToggleAll(items.filter(rowSelectable).map((r) => r.id), !!v)} aria-label={`Pilih semua ${g}`} />}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                {!(g !== null && collapsed.has(g)) && items.map(renderRow)}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
